import dayjs from "dayjs";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import toastr from "toastr";
import * as Yup from "yup";
import { GOOGLE_MAP_KEY } from "../../environtments/environtment";

// Phone input component
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

// Google Maps
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";

// API hooks
import { useCreateBooking } from "../../api/booking";
// import { useGetAllDiscounts } from "../../api/discount";
import { useGetAdminConfigUplifts } from "../../api/price";
import { useGetAllCertsNotPaginated } from "../../api/certs";
import { useGetAllSectorsNotPaginated } from "../../api/sector";
import {
  parseSupervisorsListResponse,
  useSupervisorsListForBooking,
} from "../../api/supervisors";
import {
  // calculateDiscountPricingDetails,
  calculatePricingBreakdownFromPatterns,
  calculateTotalPriceFromPatterns,
} from "../../helpers/bookingPriceCalculator";
import {
  DBS_TYPE_OPTIONS,
  WORKFORCE_OPTIONS,
  dbsTypeRequiresWorkforce,
  workForceUiToApi,
} from "../../helpers/dbsCertHelper";
import {
  JOBARK_PRIVACY_URL,
  JOBARK_TERMS_URL,
} from "../../constants/workerOnboarding";
import { experienceLevelOptions } from "./bookingFormShared";
// import { useAuthStore } from "../../service/useAuthStore"; // Removed onboarding check

/* ======================
   Helpers & Constants
====================== */
const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "380px", borderRadius: 12 };
const defaultCenter = { lat: 51.509865, lng: -0.118092 }; // London fallback

const isDbsCertSlug = (certSlug, certsCatalog = []) => {
  const slug = String(certSlug ?? "")
    .trim()
    .toLowerCase();
  if (slug === "dbs") return true;
  const meta = (certsCatalog || []).find(
    (c) =>
      String(c?.slug ?? "")
        .trim()
        .toLowerCase() === slug
  );
  return !!meta?.hasServiceNumber;
};

const patternHasDbsClearance = (certSlugs, certsCatalog = []) =>
  (certSlugs || []).some((slug) => isDbsCertSlug(slug, certsCatalog));

const toISODate = (d) => dayjs(d).format("YYYY-MM-DD");
const isOnOrAfter = (bISO, aISO) =>
  !bISO || !aISO
    ? true
    : dayjs(bISO).startOf("day").diff(dayjs(aISO).startOf("day"), "day") >= 0;

const reactSelectMenuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999, backgroundColor: "#fff" }),
};

const parseHHMM = (hhmm) => {
  const [h = "0", m = "0"] = String(hhmm || "").split(":");
  return { h: Number(h), m: Number(m) };
};
const diffHours = (start, end) => {
  // both "HH:MM", allow overnight (end < start)
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  const sMin = s.h * 60 + s.m;
  let eMin = e.h * 60 + e.m;
  if (Number.isNaN(sMin) || Number.isNaN(eMin)) return 0;
  if (eMin <= sMin) eMin += 24 * 60;
  return (eMin - sMin) / 60;
};

const hhmmToMinutes = (hhmm) => {
  const { h, m } = parseHHMM(hhmm);
  return h * 60 + m;
};
const minutesToHHMM = (mins) => {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, "0");
  const m = (mins % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

/** Single time token → "HH:MM" (supports 12h e.g. 12:00am, 24h) */
const convertTimeTokenToHHMM = (timeStr) => {
  const trimmed = String(timeStr || "")
    .trim()
    .toLowerCase();
  if (!trimmed) return "";
  const isPM = trimmed.includes("pm");
  const isAM = trimmed.includes("am");
  if (isPM || isAM) {
    const timeMatch = trimmed.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
    if (timeMatch) {
      let hour = parseInt(timeMatch[1], 10);
      const minute = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
      if (isPM && hour !== 12) hour += 12;
      if (isAM && hour === 12) hour = 0;
      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
        2,
        "0"
      )}`;
    }
  }
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const [h, m] = trimmed.split(":");
    return `${String(parseInt(h, 10)).padStart(2, "0")}:${String(
      parseInt(m, 10)
    ).padStart(2, "0")}`;
  }
  return "";
};

/** Sector nightTimings: "10pm - 7am", "12:00am 6:00am", etc. */
const parseSectorNightTimingsStr = (timingsStr) => {
  if (!timingsStr?.trim()) return { start: "", end: "" };
  const trimmed = timingsStr.trim();
  const dashParts = trimmed.split(/\s*-\s*/).filter(Boolean);
  if (dashParts.length === 2) {
    return {
      start: convertTimeTokenToHHMM(dashParts[0]),
      end: convertTimeTokenToHHMM(dashParts[1]),
    };
  }
  const m = trimmed.match(
    /(\d{1,2}(?::\d{2})?\s*(?:am|pm))\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i
  );
  if (m) {
    return {
      start: convertTimeTokenToHHMM(m[1]),
      end: convertTimeTokenToHHMM(m[2]),
    };
  }
  return { start: "", end: "" };
};

const buildDayNightSegmentsFromWindow = (nightStartMin, nightEndMin) => {
  const DAY_MS = 24 * 60;
  if (nightStartMin === nightEndMin) return null;
  if (nightStartMin < nightEndMin) {
    return [
      { start: 0, end: nightStartMin, type: "day" },
      { start: nightStartMin, end: nightEndMin, type: "night" },
      { start: nightEndMin, end: DAY_MS, type: "day" },
    ];
  }
  return [
    { start: 0, end: nightEndMin, type: "night" },
    { start: nightEndMin, end: nightStartMin, type: "day" },
    { start: nightStartMin, end: DAY_MS, type: "night" },
  ];
};

const DEFAULT_DAY_NIGHT_SEGMENTS = [
  { start: 0, end: 6 * 60, type: "night" },
  { start: 6 * 60, end: 22 * 60, type: "day" },
  { start: 22 * 60, end: 24 * 60, type: "night" },
];

const getDayNightSegmentsFromNightTimings = (nightTimingsStr) => {
  const { start, end } = parseSectorNightTimingsStr(nightTimingsStr);
  if (!start || !end) return DEFAULT_DAY_NIGHT_SEGMENTS;
  const ns = hhmmToMinutes(start);
  const ne = hhmmToMinutes(end);
  if (Number.isNaN(ns) || Number.isNaN(ne)) return DEFAULT_DAY_NIGHT_SEGMENTS;
  const built = buildDayNightSegmentsFromWindow(ns, ne);
  return built || DEFAULT_DAY_NIGHT_SEGMENTS;
};

const MAX_SHIFT_PATTERNS = 3;

const maxShiftPatternsForBooking = (isUrgent) =>
  isUrgent ? 1 : MAX_SHIFT_PATTERNS;

const emptySupervisor = () => ({
  source: "new",
  supervisorSlug: "",
  name: "",
  mobile: "",
  email: "",
});

function mapSupervisorToPayload(s, withPlus) {
  if (s.source === "existing" && s.supervisorSlug) {
    return { supervisorSlug: s.supervisorSlug };
  }
  return {
    name: s.name,
    mobile: withPlus(s.mobile),
    email: s.email,
  };
}

function supervisorIdentityKey(s) {
  if (!s) return null;
  if (s.source === "existing" && s.supervisorSlug) {
    return `slug:${s.supervisorSlug}`;
  }
  if (s.source === "new") {
    const email = String(s.email || "")
      .trim()
      .toLowerCase();
    if (email) return `email:${email}`;
    const mobile = String(s.mobile || "").replace(/\D/g, "");
    if (mobile.length >= 10) return `mobile:${mobile}`;
  }
  return null;
}

function isDuplicateSupervisorInShift(supervisors, candidateIdx) {
  const key = supervisorIdentityKey(supervisors[candidateIdx]);
  if (!key) return false;
  return supervisors.some(
    (s, i) => i !== candidateIdx && supervisorIdentityKey(s) === key
  );
}

function supervisorsFromYupContext(from) {
  if (!from?.length) return [];
  for (let i = from.length - 1; i >= 0; i--) {
    const value = from[i]?.value;
    if (Array.isArray(value?.supervisor)) return value.supervisor;
  }
  return [];
}

const BOOKING_STEP_FIELD_PREFIXES = {
  1: ["hasPORequired", "poReference", "poDocument", "timezone", "location"],
  2: ["isUrgent", "shiftPattern"],
  3: ["agreements"],
};

function setFormikErrorAtPath(target, path, message) {
  if (!path) return;
  const parts = path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const nextKey = parts[i + 1];
    if (cur[key] == null || typeof cur[key] !== "object") {
      cur[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = message;
}

function yupValidationErrorToFormik(error) {
  const formikErrors = {};
  if (!error) return formikErrors;
  if (error.inner?.length) {
    for (const issue of error.inner) {
      if (issue.path)
        setFormikErrorAtPath(formikErrors, issue.path, issue.message);
    }
  } else if (error.path) {
    setFormikErrorAtPath(formikErrors, error.path, error.message);
  }
  return formikErrors;
}

function flattenFormikErrorPaths(errors, prefix = "") {
  if (!errors) return [];
  if (typeof errors === "string") return prefix ? [prefix] : [];
  if (Array.isArray(errors)) {
    return errors.flatMap((item, i) =>
      flattenFormikErrorPaths(item, prefix ? `${prefix}.${i}` : String(i))
    );
  }
  if (typeof errors !== "object") return [];
  return Object.entries(errors).flatMap(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof val === "string") return [path];
    return flattenFormikErrorPaths(val, path);
  });
}

function errorPathBelongsToStep(path, step) {
  const prefixes = BOOKING_STEP_FIELD_PREFIXES[step] || [];
  return prefixes.some(
    (p) => path === p || path.startsWith(`${p}.`) || path.startsWith(`${p}[`)
  );
}

function mergeStepErrors(previousErrors, step, stepErrors) {
  const merged = { ...(previousErrors || {}) };
  for (const key of Object.keys(merged)) {
    if ((BOOKING_STEP_FIELD_PREFIXES[step] || []).includes(key)) {
      delete merged[key];
    }
  }
  return { ...merged, ...stepErrors };
}

function setFormikLocationErrors(formik, locationFieldErrors) {
  const next = { ...formik.errors };
  if (!locationFieldErrors || Object.keys(locationFieldErrors).length === 0) {
    delete next.location;
  } else {
    next.location = locationFieldErrors;
  }
  formik.setErrors(next);
}

async function validateFormikLocation(formik, locationSchema, locationValues) {
  try {
    await locationSchema.validate(locationValues, { abortEarly: false });
    setFormikLocationErrors(formik, null);
    return true;
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      const locationErrors = {};
      if (err.inner?.length) {
        for (const issue of err.inner) {
          if (issue.path) locationErrors[issue.path] = issue.message;
        }
      } else if (err.path) {
        locationErrors[err.path] = err.message;
      }
      setFormikLocationErrors(formik, locationErrors);
    }
    return false;
  }
}

function getStepTouched(step, values) {
  if (step === 1) {
    return {
      hasPORequired: true,
      poReference: true,
      poDocument: true,
      timezone: true,
      location: {
        siteName: true,
        fullAddress: true,
        latitude: true,
        longitude: true,
        searchAddress: true,
        accessNotes: true,
      },
      notes: true,
    };
  }
  if (step === 2) {
    return {
      isUrgent: true,
      shiftPattern: (values.shiftPattern || []).map((p) => ({
        sectorSlug: true,
        roleSlug: true,
        quantity: true,
        hasPPEProvided: true,
        ppeInstruction: true,
        startDate: true,
        endDate: true,
        excludeWeekend: true,
        excludeSpecificDates: true,
        dates: true,
        startTime: true,
        endTime: true,
        breakTime: true,
        experienceLevel: true,
        certSlugs: true,
        dbsType: true,
        dbsWorkForce: true,
        shiftDetails: true,
        supervisor: (p.supervisor || []).map(() => ({
          source: true,
          supervisorSlug: true,
          name: true,
          mobile: true,
          email: true,
        })),
      })),
    };
  }
  if (step === 3) {
    return {
      agreements: {
        authorised: true,
        detailsAccurate: true,
        legalAgreements: true,
      },
    };
  }
  return {};
}

function friendlyStepErrorLabel(path) {
  const map = {
    poReference: "Purchase Order Reference",
    poDocument: "Purchase Order Document",
    "location.siteName": "Site Name",
    "location.fullAddress": "Full Address",
    "location.latitude": "Location Latitude",
    "location.longitude": "Location Longitude",
    timezone: "Timezone",
    "agreements.authorised": "Authorisation to place bookings",
    "agreements.detailsAccurate": "Booking details accuracy",
    "agreements.legalAgreements": "Legal agreements",
  };
  if (map[path]) return map[path];
  const patternMatch = path.match(/^shiftPattern\.(\d+)\.(.+)$/);
  if (patternMatch) {
    const idx = Number(patternMatch[1]) + 1;
    const field = patternMatch[2].replace(/\.\d+\./g, " ").replace(/\./g, " ");
    return `Shift Pattern ${idx}: ${field}`;
  }
  return path;
}

function toastStepValidationErrors(step, stepErrors) {
  const paths = flattenFormikErrorPaths(stepErrors).filter((p) =>
    errorPathBelongsToStep(p, step)
  );
  if (!paths.length) {
    toastr.error("Please complete the required fields before continuing.");
    return;
  }
  const labels = [...new Set(paths.map(friendlyStepErrorLabel))];
  if (step === 1) {
    toastr.error(
      `Please complete the required fields: ${labels.slice(0, 6).join(", ")}${
        labels.length > 6 ? "…" : ""
      }`
    );
    return;
  }
  toastr.error(
    `Please fix validation errors: ${labels.slice(0, 4).join(", ")}${
      labels.length > 4 ? "…" : ""
    }`
  );
}

// min start based on booking type
const minStartISOForType = (bookingType) => {
  const now = dayjs();
  if (bookingType === "urgent") {
    // allow today if now+6h still today; else tomorrow
    const min = now.add(6, "hour");
    return toISODate(min);
  }
  // not urgent: tomorrow is allowed if start time is after current time
  return toISODate(now.add(1, "day"));
};

// Validate urgent booking constraints
const validateUrgentBooking = (
  startDate,
  startTime,
  isUrgent,
  endTime = null
) => {
  const now = dayjs();
  const selectedDate = dayjs(startDate);
  const selectedTime = dayjs(`${startDate} ${startTime}`);

  if (isUrgent) {
    // Urgent booking: must be 6-24 hours from now
    const minTime = now.add(6, "hour");
    const maxTime = now.add(24, "hour");

    if (selectedTime.isBefore(minTime)) {
      return `For urgent bookings, start time must be at least 6 hours from now (${minTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    if (selectedTime.isAfter(maxTime)) {
      return `For urgent bookings, start time must be within 24 hours from now (${maxTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    // If selecting today's date, start time must be after current time
    if (selectedDate.isSame(now, "day")) {
      const currentTime = now.format("HH:mm");
      if (startTime <= currentTime) {
        return `For urgent bookings on the same day, start time must be after current time (${currentTime})`;
      }
    }

    // Validate end time: entire shift must complete within 24 hours from now
    if (endTime) {
      let endDateTime;
      if (endTime < startTime) {
        // Overnight shift - end time is next day
        endDateTime = dayjs(`${startDate} ${endTime}`).add(1, "day");
      } else {
        // Same day shift
        endDateTime = dayjs(`${startDate} ${endTime}`);
      }

      if (endDateTime.isAfter(maxTime)) {
        return `For urgent bookings, the entire shift must complete within 24 hours from now. End time cannot be after ${maxTime.format(
          "DD MMM YYYY, h:mm A"
        )}`;
      }
    }
  } else {
    // Non-urgent booking: must be more than 24 hours from now
    const minTime = now.add(24, "hour");

    if (selectedTime.isBefore(minTime) || selectedTime.isSame(minTime)) {
      return `For non-urgent bookings, start time must be more than 24 hours from now (${minTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    // If selecting tomorrow's date, start time must be after current time
    const tomorrow = now.add(1, "day");
    if (selectedDate.isSame(tomorrow, "day")) {
      const currentTime = now.format("HH:mm");
      if (startTime <= currentTime) {
        return `For non-urgent bookings on tomorrow, start time must be after current time (${currentTime})`;
      }
    }
  }

  return null; // Valid
};

// sectors → roles (keep sectorSlug for payload)
const flattenRoles = (sectors) => {
  const items = [];
  (sectors || []).forEach((s) => {
    (s.roles || []).forEach((r) => {
      items.push({
        value: r.slug,
        label: `${r.title} — ${s.title}`,
        group: s.title,
        sectorSlug: s.slug,
      });
    });
  });
  return items;
};

// break-time rule helper (returns minimal minutes required given shift hours)
const minBreakMinutesFor = (shiftHours) => {
  if (shiftHours <= 6) return 0; // can be 0
  if (shiftHours > 9) return 30;
  return 20;
};

// Generate dates array from start/end date with weekend exclusion
const generateDatesArray = (
  startDate,
  endDate,
  excludeWeekend = false,
  excludeSpecificDates = ""
) => {
  if (!startDate || !endDate) return [];

  const dates = [];
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const excludeDates = excludeSpecificDates
    ? excludeSpecificDates.split(",").map((d) => d.trim())
    : [];

  let current = start;
  while (current.isSame(end, "day") || current.isBefore(end, "day")) {
    const dateStr = current.format("YYYY-MM-DD");

    // Skip if it's a weekend and excludeWeekend is true
    if (excludeWeekend && (current.day() === 0 || current.day() === 6)) {
      current = current.add(1, "day");
      continue;
    }

    // Skip if date is in excludeSpecificDates
    if (excludeDates.includes(dateStr)) {
      current = current.add(1, "day");
      continue;
    }

    dates.push(dateStr);
    current = current.add(1, "day");
  }

  return dates;
};

// Custom Date Picker Component for Exclude Dates
const ExcludeDatesPicker = ({
  startDate,
  endDate,
  excludeWeekend,
  selectedDates,
  onDatesChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!startDate || !endDate) {
    return (
      <div className="text-muted">Please select start and end dates first</div>
    );
  }

  const generateDateRange = () => {
    const dates = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    let current = start;
    while (current.isSame(end, "day") || current.isBefore(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");

      // Skip weekends if excludeWeekend is true
      if (excludeWeekend && (current.day() === 0 || current.day() === 6)) {
        current = current.add(1, "day");
        continue;
      }

      dates.push({
        date: dateStr,
        dayjs: current,
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current = current.add(1, "day");
    }

    return dates;
  };

  const availableDates = generateDateRange();
  const selectedDatesArray = selectedDates
    ? selectedDates.split(",").map((d) => d.trim())
    : [];

  const toggleDate = (dateStr) => {
    const newSelectedDates = selectedDatesArray.includes(dateStr)
      ? selectedDatesArray.filter((d) => d !== dateStr)
      : [...selectedDatesArray, dateStr];

    onDatesChange(newSelectedDates.join(","));
  };

  return (
    <div className="exclude-dates-picker">
      <Button
        type="button"
        color="outline-secondary"
        onClick={() => setIsOpen(!isOpen)}
        className="w-100"
      >
        {selectedDatesArray.length > 0
          ? `Exclude ${selectedDatesArray.length} date${
              selectedDatesArray.length > 1 ? "s" : ""
            }`
          : "Select dates to exclude"}
      </Button>

      {isOpen && (
        <div
          className="date-picker-modal mt-2 p-3 border rounded bg-white shadow"
          style={{ zIndex: 1000, position: "relative" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Select dates to exclude</h6>
            <Button
              type="button"
              size="sm"
              color="outline-secondary"
              onClick={() => setIsOpen(false)}
            >
              ×
            </Button>
          </div>

          <div
            className="date-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              maxWidth: "300px",
            }}
          >
            {availableDates.map((dateObj) => {
              const isSelected = selectedDatesArray.includes(dateObj.date);
              const isToday = dateObj.dayjs.isSame(dayjs(), "day");

              return (
                <button
                  key={dateObj.date}
                  type="button"
                  className={`btn btn-sm ${
                    isSelected
                      ? "btn-danger"
                      : isToday
                      ? "btn-warning"
                      : dateObj.isWeekend
                      ? "btn-outline-secondary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => toggleDate(dateObj.date)}
                  style={{
                    fontSize: "0.8rem",
                    padding: "6px 4px",
                    minHeight: "32px",
                  }}
                  title={`${dateObj.dayjs.format("DD MMM YYYY")}${
                    isSelected ? " (Excluded)" : ""
                  }`}
                >
                  {dateObj.dayjs.format("DD")}
                </button>
              );
            })}
          </div>

          <div className="mt-3">
            <small className="text-muted">
              <span className="badge bg-primary me-1">Available</span>
              <span className="badge bg-warning me-1">Today</span>
              <span className="badge bg-danger me-1">Excluded</span>
              <span className="badge bg-secondary">Weekend</span>
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

ExcludeDatesPicker.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  excludeWeekend: PropTypes.bool,
  selectedDates: PropTypes.string,
  onDatesChange: PropTypes.func.isRequired,
};

/* ======================
   Component
====================== */
const BookingCreate = () => {
  document.title = "Submit Staffing Request | JobArk";
  const navigate = useNavigate();
  // const hasCompletedOnBoarding = useAuthStore((s) => s.hasCompletedOnBoarding); // Removed onboarding check

  // Google Maps
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  // Sectors/Roles source
  const { data: sectorsRes, isLoading: sectorsLoading } =
    useGetAllSectorsNotPaginated();

  // useEffect(() => {
  //   loadSectors();
  // }, [loadSectors]);

  const roleOptions = useMemo(
    () => flattenRoles(sectorsRes?.data || []),
    [sectorsRes]
  );

  const sectorOptions = useMemo(
    () =>
      (sectorsRes?.data || []).map((s) => ({
        value: s.slug,
        label: s.title,
      })),
    [sectorsRes]
  );

  const { mutate: loadComplianceCerts, data: complianceCertsRes } =
    useGetAllCertsNotPaginated();
  const complianceCertsList = useMemo(() => {
    const list =
      complianceCertsRes?.data?.data || complianceCertsRes?.data || [];
    return (Array.isArray(list) ? list : []).filter(
      (cert) => cert?.isActive !== false && cert?.slug
    );
  }, [complianceCertsRes]);
  const complianceCertOptions = useMemo(
    () =>
      complianceCertsList.map((cert) => ({
        value: cert.slug,
        label: cert.title || cert.slug,
      })),
    [complianceCertsList]
  );

  useEffect(() => {
    loadComplianceCerts({ isComplianceCertificate: true });
  }, [loadComplianceCerts]);

  const upliftsQuery = useGetAdminConfigUplifts();

  const priceData = useMemo(
    () => upliftsQuery.data?.data ?? null,
    [upliftsQuery.data]
  );

  // Discounts data (commented out — discount banner/functionality disabled)
  // const { data: discountsRes } = useGetAllDiscounts();

  const { data: supervisorsRes, isLoading: supervisorsLoading } =
    useSupervisorsListForBooking();

  const supervisorOptions = useMemo(() => {
    return parseSupervisorsListResponse(supervisorsRes).map((sup) => ({
      value: sup.slug,
      label: `${sup.fullName || sup.slug} — ${sup.email || ""}`,
      supervisor: sup,
    }));
  }, [supervisorsRes]);

  // Success modal
  const [showSuccess, setShowSuccess] = useState(false);
  const handleCloseModal = () => {
    setShowSuccess(false);
    navigate("/client/bookings");
  };

  // Stepper
  const [step, setStep] = useState(1);
  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  // Create
  const { mutate: createBookingMutation, isPending } = useCreateBooking();

  // ====== Initial Values ======
  const initialValues = {
    isUrgent: false, // checkbox for urgent booking
    bookingType: "not_urgent", // "urgent" | "not_urgent" - derived from isUrgent
    hasPORequired: false,
    poReference: "",
    poDocument: null, // File

    // Up to 3 patterns
    shiftPattern: [
      {
        sectorSlug: "",
        roleSlug: "",
        quantity: 1,
        experienceLevel: [],
        certSlugs: [],
        dbsType: "",
        dbsWorkForce: "",
        hasPPEProvided: "not_required", // "yes" | "not_required"
        ppeInstruction: "",
        startDate: "",
        endDate: "",
        excludeWeekend: false,
        excludeSpecificDates: "",
        dates: [], // ["YYYY-MM-DD"] - will be generated from startDate/endDate
        startTime: "09:00",
        endTime: "17:00",
        breakTime: "00:30",
        supervisor: [emptySupervisor()],
        shiftDetails: "",
      },
    ],

    timezone: "Europe/London",
    location: {
      siteName: "",
      searchAddress: "",
      fullAddress: "",
      accessNotes: "",
      latitude: defaultCenter.lat,
      longitude: defaultCenter.lng,
    },
    notes: "",

    // Agreements (Step 3)
    agreements: {
      authorised: false,
      detailsAccurate: false,
      legalAgreements: false,
    },
  };

  // ====== Yup Schema (exact payload fields) ======
  const PatternSchema = Yup.object({
    sectorSlug: Yup.string().required("Sector is required"),
    roleSlug: Yup.string().required("Role is required"),
    quantity: Yup.number().integer().min(1).required("Quantity required"),
    hasPPEProvided: Yup.mixed().oneOf(["yes", "not_required"]).required(),
    ppeInstruction: Yup.string().when("hasPPEProvided", {
      is: "yes",
      then: (s) =>
        s.trim().required("PPE instruction required when PPE is provided"),
      otherwise: (s) => s.notRequired(),
    }),
    startDate: Yup.string()
      .required("Start date is required")
      .test(
        "booking-validation",
        "Invalid start date for booking",
        function (v) {
          const { startTime, endTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startTime) return true;

          const error = validateUrgentBooking(v, startTime, isUrgent, endTime);
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    endDate: Yup.string()
      .required("End date is required")
      .test("after-start", "End date must be after start date", function (v) {
        const { startDate } = this.parent;
        return (
          v &&
          startDate &&
          (dayjs(v).isSame(dayjs(startDate), "day") ||
            dayjs(v).isAfter(dayjs(startDate), "day"))
        );
      }),
    excludeWeekend: Yup.boolean(),
    excludeSpecificDates: Yup.string(),
    dates: Yup.array()
      .of(Yup.string().required())
      .min(1, "Add at least one date"),
    startTime: Yup.string()
      .required("Start time required")
      .test(
        "booking-time-validation",
        "Invalid start time for booking",
        function (v) {
          const { startDate, endTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startDate) return true;

          const error = validateUrgentBooking(startDate, v, isUrgent, endTime);
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    endTime: Yup.string()
      .required("End time required")
      .test(
        "after-start",
        "End must be after Start (overnight allowed)",
        function (v) {
          const { startTime } = this.parent;
          return v && startTime && v !== startTime;
        }
      )
      .test(
        "urgent-24hour-window",
        "Invalid end time for urgent booking",
        function (v) {
          const { startDate, startTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startDate || !startTime || !isUrgent) return true;

          const error = validateUrgentBooking(
            startDate,
            startTime,
            isUrgent,
            v
          );
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    breakTime: Yup.string()
      .required("Break time required")
      .test("min-break", "Break time below required minimum", function (v) {
        const { startTime, endTime } = this.parent;
        if (!startTime || !endTime || !v) return true;
        const hours = diffHours(startTime, endTime);
        const minMins = minBreakMinutesFor(hours);
        return hhmmToMinutes(v) >= minMins;
      }),
    supervisor: Yup.array()
      .of(
        Yup.object({
          source: Yup.mixed().oneOf(["existing", "new"]).required(),
          supervisorSlug: Yup.string().when("source", {
            is: "existing",
            then: (s) =>
              s
                .trim()
                .required("Select a supervisor")
                .test(
                  "unique-in-shift",
                  "This supervisor is already added to this shift",
                  function (value) {
                    if (!value) return true;
                    const supervisors = supervisorsFromYupContext(this.from);
                    const count = supervisors.filter(
                      (row) =>
                        row?.source === "existing" &&
                        row?.supervisorSlug === value
                    ).length;
                    return count <= 1;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
          name: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .trim()
                .required("Name required")
                .min(2, "Name must be at least 2 characters"),
            otherwise: (s) => s.notRequired(),
          }),
          mobile: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .required("Mobile required")
                .test(
                  "phone-valid",
                  "Please enter a valid phone number",
                  function (value) {
                    if (!value) return false;
                    const digitsOnly = value.replace(/\D/g, "");
                    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
          email: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .email("Please enter a valid email address")
                .required("Email is required")
                .test(
                  "unique-in-shift",
                  "A supervisor with this email is already added to this shift",
                  function (value) {
                    if (!value) return true;
                    const email = value.trim().toLowerCase();
                    const supervisors = supervisorsFromYupContext(this.from);
                    const count = supervisors.filter(
                      (row) =>
                        row?.source === "new" &&
                        String(row?.email || "")
                          .trim()
                          .toLowerCase() === email
                    ).length;
                    return count <= 1;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
        })
      )
      .min(1, "Add at least one supervisor")
      .test(
        "unique-supervisors",
        "Each supervisor can only be added once per shift",
        function (supervisors) {
          if (!supervisors?.length) return true;
          const seen = new Map();
          for (let i = 0; i < supervisors.length; i++) {
            const key = supervisorIdentityKey(supervisors[i]);
            if (!key) continue;
            if (seen.has(key)) {
              const isExisting = supervisors[i]?.source === "existing";
              return this.createError({
                path: `${i}.${isExisting ? "supervisorSlug" : "email"}`,
                message: isExisting
                  ? "This supervisor is already added to this shift"
                  : "A supervisor with these details is already added to this shift",
              });
            }
            seen.set(key, i);
          }
          return true;
        }
      ),
    shiftDetails: Yup.string().required("Shift details required"),
    experienceLevel: Yup.array()
      .of(Yup.string())
      .min(1, "Experience level is required")
      .required("Experience level is required"),
    certSlugs: Yup.array()
      .of(Yup.string())
      .min(1, "At least one mandatory clearance is required")
      .required("At least one mandatory clearance is required"),
    dbsType: Yup.string().when("certSlugs", {
      is: (slugs) => patternHasDbsClearance(slugs, complianceCertsList),
      then: (s) => s.required("DBS type is required"),
      otherwise: (s) => s.notRequired(),
    }),
    dbsWorkForce: Yup.string().when(["certSlugs", "dbsType"], {
      is: (slugs, dbsType) =>
        patternHasDbsClearance(slugs, complianceCertsList) &&
        dbsTypeRequiresWorkforce(dbsType),
      then: (s) =>
        s.required("Workforce is required for Enhanced or Enhanced Plus DBS"),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const validationSchema = Yup.object({
    isUrgent: Yup.boolean().required(),
    hasPORequired: Yup.boolean().required(),
    poReference: Yup.string().when("hasPORequired", {
      is: true,
      then: (s) => s.trim().required("PO reference required"),
      otherwise: (s) => s.notRequired(),
    }),
    poDocument: Yup.mixed().when("hasPORequired", {
      is: true,
      then: (s) => s.required("Official PO document required"),
      otherwise: (s) => s.notRequired(),
    }),

    shiftPattern: Yup.array()
      .of(PatternSchema)
      .min(1)
      .when("isUrgent", {
        is: true,
        then: (schema) =>
          schema.max(1, "Urgent bookings can only have one shift pattern"),
        otherwise: (schema) =>
          schema.max(
            MAX_SHIFT_PATTERNS,
            "You can add up to three shift patterns"
          ),
      }),

    timezone: Yup.string().required("Timezone required"),
    location: Yup.object({
      siteName: Yup.string().required("Site name required"),
      searchAddress: Yup.string().notRequired(),
      fullAddress: Yup.string().required("Full address required"),
      accessNotes: Yup.string().notRequired(),
      latitude: Yup.number().required("Latitude required"),
      longitude: Yup.number().required("Longitude required"),
    }),
    notes: Yup.string().notRequired(),
    agreements: Yup.object({
      authorised: Yup.boolean().oneOf([true], "Required"),
      detailsAccurate: Yup.boolean().oneOf([true], "Required"),
      legalAgreements: Yup.boolean().oneOf([true], "Required"),
    }),
  });

  // ====== Formik ======
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // 6-hour rule across all dates
      const minISO = minStartISOForType(
        values.isUrgent ? "urgent" : "not_urgent"
      );
      const earliest = values.shiftPattern.flatMap((p) => p.dates).sort()[0];

      if (!isOnOrAfter(earliest, minISO)) {
        toastr.error(
          `Earliest allowed date is ${dayjs(minISO).format(
            "ddd, DD MMM YYYY"
          )} for this booking type.`
        );
        return;
      }

      // Normalize phone numbers to include "+"
      const withPlus = (d) => (d ? (d.startsWith("+") ? d : `+${d}`) : d);

      // Map to EXACT payload
      const payload = {
        bookingType: values.isUrgent ? "urgent" : "not_urgent",
        hasPORequired: values.hasPORequired,
        ...(values.hasPORequired ? { poReference: values.poReference } : {}),
        ...(values.hasPORequired ? { poDocument: values.poDocument } : {}),

        // Pricing information (note: backend has typo "actualIntialAmount")
        actualIntialAmount: Math.round(pricingDetails.originalPrice),
        discountType: pricingDetails.discountType || "percentage",
        discountPercentage: Number(pricingDetails.discountPercentage),
        discountedAmount: Math.round(pricingDetails.discountAmount),

        shiftPattern: values.shiftPattern.map((p) => ({
          sectorSlug: p.sectorSlug,
          roleSlug: p.roleSlug,
          quantity: Number(p.quantity),
          hasPPEProvided: p.hasPPEProvided === "yes",
          ...(p.hasPPEProvided === "yes"
            ? { ppeInstruction: p.ppeInstruction }
            : {}),
          dates: generateDatesArray(
            p.startDate,
            p.endDate,
            p.excludeWeekend,
            p.excludeSpecificDates
          ),
          startTime: p.startTime,
          endTime: p.endTime,
          breakTime: p.breakTime,
          supervisor: p.supervisor.map((s) =>
            mapSupervisorToPayload(s, withPlus)
          ),
          shiftDetails: p.shiftDetails,
          experienceLevel: p.experienceLevel,
          ...(p.certSlugs?.length ? { certSlugs: p.certSlugs } : {}),
          ...(patternHasDbsClearance(p.certSlugs, complianceCertsList) &&
          p.dbsType
            ? {
                dbsType: p.dbsType,
                ...(dbsTypeRequiresWorkforce(p.dbsType)
                  ? { workForce: workForceUiToApi(p.dbsWorkForce) }
                  : {}),
              }
            : {}),
        })),

        timezone: values.timezone || "Europe/London",
        location: {
          siteName: values.location.siteName,
          searchAddress: values.location.searchAddress,
          fullAddress: values.location.fullAddress,
          accessNotes: values.location.accessNotes,
          latitude: Number(values.location.latitude),
          longitude: Number(values.location.longitude),
        },
        notes: values.notes,
      };

      // Always use FormData for consistency
      const fd = new FormData();
      fd.append("bookingType", payload.bookingType);
      fd.append("hasPORequired", String(payload.hasPORequired));
      if (payload.hasPORequired) {
        fd.append("poReference", payload.poReference);
        fd.append("document", values.poDocument);
      }
      fd.append("timezone", payload.timezone);
      fd.append("location", JSON.stringify(payload.location));
      fd.append("shiftPattern", JSON.stringify(payload.shiftPattern));
      if (payload.notes) fd.append("notes", payload.notes);

      // Pricing information
      fd.append("actualIntialAmount", String(payload.actualIntialAmount));
      if (payload.discountType) {
        fd.append("discountType", payload.discountType);
      }
      fd.append("discountPercentage", String(payload.discountPercentage));
      fd.append("discountedAmount", String(payload.discountedAmount));

      const body = fd;
      const opts = { isMultipart: true };

      console.log("Booking Payload - Pricing Info:", {
        actualIntialAmount: payload.actualIntialAmount,
        discountPercentage: payload.discountPercentage,
        discountedAmount: payload.discountedAmount,
        totalWorkers: pricingDetails.totalWorkers,
      });

      createBookingMutation(body, {
        onSuccess: () => setShowSuccess(true),
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
        ...opts,
      });
    },
  });

  // Extra business-rule validations for patterns (step 2)
  const validatePatternsBusinessRules = useCallback((values) => {
    const maxPatterns = maxShiftPatternsForBooking(values.isUrgent);
    if ((values.shiftPattern || []).length > maxPatterns) {
      return values.isUrgent
        ? "Urgent bookings can only have one shift pattern."
        : "Maximum 3 shift patterns allowed.";
    }
    for (const [i, p] of (values.shiftPattern || []).entries()) {
      const dur = diffHours(p.startTime, p.endTime);
      if (dur < 4 || dur > 12) {
        return `Shift Pattern ${
          i + 1
        }: shift duration must be between 4 and 12 hours.`;
      }
      const minBreak = minBreakMinutesFor(dur);
      if (hhmmToMinutes(p.breakTime) < minBreak) {
        return `Shift Pattern ${
          i + 1
        }: break must be at least ${minBreak} minutes.`;
      }

      // Generate dates array for validation
      const generatedDates = generateDatesArray(
        p.startDate,
        p.endDate,
        p.excludeWeekend,
        p.excludeSpecificDates
      );
      if (generatedDates.length === 0 && p.startDate && p.endDate) {
        return `Shift Pattern ${
          i + 1
        }: no valid dates found in the specified range.`;
      }

      // Urgent booking validation
      if (p.startDate && p.startTime) {
        const urgentError = validateUrgentBooking(
          p.startDate,
          p.startTime,
          values.isUrgent,
          p.endTime
        );
        if (urgentError) {
          return `Shift Pattern ${i + 1}: ${urgentError}`;
        }
      }
    }
    // Cross-check earliest date/time against booking type rules
    const minISO = minStartISOForType(
      values.isUrgent ? "urgent" : "not_urgent"
    );
    const allDates = values.shiftPattern
      .flatMap((p) =>
        generateDatesArray(
          p.startDate,
          p.endDate,
          p.excludeWeekend,
          p.excludeSpecificDates
        )
      )
      .sort();
    const earliest = allDates[0];

    if (earliest && !isOnOrAfter(earliest, minISO)) {
      // Check if it's a time issue for tomorrow bookings
      if (values.isUrgent === false && earliest === minISO) {
        // For non-urgent bookings on tomorrow, check if start time is valid
        const tomorrowPattern = values.shiftPattern.find(
          (p) => p.startDate === earliest && p.startTime
        );
        if (tomorrowPattern) {
          const urgentError = validateUrgentBooking(
            earliest,
            tomorrowPattern.startTime,
            false,
            tomorrowPattern.endTime
          );
          if (urgentError) {
            return urgentError;
          }
        }
      } else {
        return `Earliest allowed date is ${dayjs(minISO).format(
          "ddd, DD MMM YYYY"
        )} for this booking type.`;
      }
    }
    return null;
  }, []);

  const getStepSchema = useCallback(
    (currentStep) => {
      switch (currentStep) {
        case 1:
          return Yup.object({
            hasPORequired: validationSchema.fields.hasPORequired,
            poReference: validationSchema.fields.poReference,
            poDocument: validationSchema.fields.poDocument,
            timezone: validationSchema.fields.timezone,
            location: validationSchema.fields.location,
          });
        case 2:
          return Yup.object({
            isUrgent: validationSchema.fields.isUrgent,
            shiftPattern: validationSchema.fields.shiftPattern,
          });
        case 3:
          return Yup.object({
            agreements: validationSchema.fields.agreements,
          });
        default:
          return null;
      }
    },
    [validationSchema]
  );

  const nextStep = useCallback(async () => {
    const schema = getStepSchema(step);
    if (!schema) return;

    let stepErrors = {};
    try {
      await schema.validate(formik.values, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        stepErrors = yupValidationErrorToFormik(err);
      } else {
        toastr.error("Please fix validation errors to continue.");
        return;
      }
    }

    const stepErrorPaths = flattenFormikErrorPaths(stepErrors).filter((p) =>
      errorPathBelongsToStep(p, step)
    );

    if (step === 2) {
      const businessMsg = validatePatternsBusinessRules(formik.values);
      if (businessMsg) {
        formik.setTouched(
          { ...formik.touched, ...getStepTouched(step, formik.values) },
          false
        );
        toastr.error(`Validation Error: ${businessMsg}`);
        return;
      }
    }

    if (stepErrorPaths.length > 0) {
      formik.setErrors(mergeStepErrors(formik.errors, step, stepErrors));
      formik.setTouched(
        { ...formik.touched, ...getStepTouched(step, formik.values) },
        false
      );
      toastStepValidationErrors(step, stepErrors);
      return;
    }

    formik.setErrors(mergeStepErrors(formik.errors, step, {}));
    setStep((s) => Math.min(3, s + 1));
  }, [step, formik, getStepSchema, validatePatternsBusinessRules]);

  const setField = useCallback(
    (path, v, shouldValidate = true) =>
      formik.setFieldValue(path, v, shouldValidate),
    [formik]
  );

  const locationSchema = validationSchema.fields.location;

  const patchLocation = useCallback(
    async (patch, { validate = true } = {}) => {
      const nextLocation = { ...formik.values.location, ...patch };
      await formik.setFieldValue("location", nextLocation, false);
      formik.setFieldTouched(
        "location",
        {
          ...(formik.touched.location || {}),
          ...Object.fromEntries(Object.keys(patch).map((k) => [k, true])),
        },
        false
      );
      if (validate) {
        await validateFormikLocation(formik, locationSchema, nextLocation);
      }
    },
    [formik, locationSchema]
  );

  const locationAddressError =
    formik.errors.location?.fullAddress ||
    (typeof formik.errors.location === "string"
      ? formik.errors.location
      : null);
  const showLocationAddressError =
    !!locationAddressError &&
    (formik.touched.location?.searchAddress ||
      formik.touched.location?.fullAddress);

  // ====== Map bits ======
  const [auto, setAuto] = useState(null);
  const onLoadAutocomplete = (ac) => setAuto(ac);
  const applyPlaceToLocation = useCallback(
    (place) => {
      if (!place?.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || place.name || "";
      if (!address.trim()) return;
      void patchLocation({
        searchAddress: address,
        fullAddress: address,
        latitude: lat,
        longitude: lng,
      });
    },
    [patchLocation]
  );

  const reverseGeocodeToAddress = useCallback(
    (lat, lng) => {
      if (
        typeof window === "undefined" ||
        !window.google?.maps?.Geocoder ||
        !Number.isFinite(Number(lat)) ||
        !Number.isFinite(Number(lng))
      ) {
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: Number(lat), lng: Number(lng) } },
        (results, status) => {
          if (status !== "OK" || !results?.[0]) return;
          const address = results[0].formatted_address || "";
          if (!address.trim()) return;
          const currentSearch = String(
            formik.values.location.searchAddress || ""
          ).trim();
          void patchLocation({
            fullAddress: address,
            searchAddress: currentSearch || address,
            latitude: Number(lat),
            longitude: Number(lng),
          });
        }
      );
    },
    [formik.values.location.searchAddress, patchLocation]
  );

  const onPlaceChanged = () => {
    // Places may not be ready synchronously when onPlaceChanged fires
    window.setTimeout(() => {
      if (!auto) return;
      applyPlaceToLocation(auto.getPlace());
    }, 0);
  };

  const center = useMemo(
    () => ({
      lat: Number(formik.values.location.latitude || defaultCenter.lat),
      lng: Number(formik.values.location.longitude || defaultCenter.lng),
    }),
    [formik.values.location.latitude, formik.values.location.longitude]
  );

  const onMarkerDragEnd = useCallback(
    (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setField("location.latitude", lat);
      setField("location.longitude", lng);
      reverseGeocodeToAddress(lat, lng);
    },
    [setField, reverseGeocodeToAddress]
  );

  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      toastr.error("Geolocation not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setField("location.latitude", latitude);
        setField("location.longitude", longitude);
        reverseGeocodeToAddress(latitude, longitude);
        toastr.success("Location captured.");
      },
      (err) => {
        toastr.error(err?.message || "Unable to get location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ====== Pattern mutators ======
  const addPattern = () => {
    const list = [...formik.values.shiftPattern];
    const maxPatterns = maxShiftPatternsForBooking(formik.values.isUrgent);
    if (list.length >= maxPatterns) {
      toastr.info(
        formik.values.isUrgent
          ? "Urgent bookings can only have one shift pattern."
          : "You can add up to 3 shift patterns."
      );
      return;
    }
    list.push({
      sectorSlug: "",
      roleSlug: "",
      quantity: 1,
      experienceLevel: [],
      certSlugs: [],
      dbsType: "",
      dbsWorkForce: "",
      hasPPEProvided: "not_required",
      ppeInstruction: "",
      startDate: "",
      endDate: "",
      excludeWeekend: false,
      excludeSpecificDates: "",
      dates: [],
      startTime: "09:00",
      endTime: "17:00",
      breakTime: "00:30",
      supervisor: [emptySupervisor()],
      shiftDetails: "",
    });
    setField("shiftPattern", list);
  };

  const removePattern = (idx) => {
    const list = [...formik.values.shiftPattern];
    list.splice(idx, 1);
    setField("shiftPattern", list);
  };

  // Helper function to update dates array when date fields change
  const updateDatesArray = (patternIdx, patternData) => {
    const dates = generateDatesArray(
      patternData.startDate,
      patternData.endDate,
      patternData.excludeWeekend,
      patternData.excludeSpecificDates
    );
    return dates;
  };

  const addSupervisor = (pIdx) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor.push(emptySupervisor());
    setField("shiftPattern", list);
  };

  const setSupervisorSource = (pIdx, sIdx, source) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor[sIdx] =
      source === "existing"
        ? {
            source: "existing",
            supervisorSlug: "",
            name: "",
            mobile: "",
            email: "",
          }
        : emptySupervisor();
    setField("shiftPattern", list);
  };

  const selectExistingSupervisor = (pIdx, sIdx, option) => {
    const list = [...formik.values.shiftPattern];
    if (!option) {
      list[pIdx].supervisor[sIdx] = {
        source: "existing",
        supervisorSlug: "",
        name: "",
        mobile: "",
        email: "",
      };
    } else {
      const alreadyUsed = list[pIdx].supervisor.some(
        (row, i) =>
          i !== sIdx &&
          row.source === "existing" &&
          row.supervisorSlug === option.value
      );
      if (alreadyUsed) {
        toastr.error("This supervisor is already added to this shift");
        return;
      }
      const sup = option.supervisor;
      list[pIdx].supervisor[sIdx] = {
        source: "existing",
        supervisorSlug: option.value,
        name: sup.fullName || "",
        mobile: (sup.phoneNumber || "").replace(/^\+/, ""),
        email: sup.email || "",
      };
    }
    setField("shiftPattern", list);
    setTimeout(() => {
      formik.validateField(`shiftPattern.${pIdx}.supervisor`);
    }, 0);
  };

  const supervisorOptionsForShift = useCallback(
    (patternIdx, supervisorIdx) => {
      const rows = formik.values.shiftPattern?.[patternIdx]?.supervisor || [];
      const usedSlugs = new Set(
        rows
          .filter(
            (row, i) =>
              i !== supervisorIdx &&
              row.source === "existing" &&
              row.supervisorSlug
          )
          .map((row) => row.supervisorSlug)
      );
      const currentSlug = rows[supervisorIdx]?.supervisorSlug;
      return supervisorOptions.filter(
        (opt) => !usedSlugs.has(opt.value) || opt.value === currentSlug
      );
    },
    [formik.values.shiftPattern, supervisorOptions]
  );

  const removeSupervisor = (pIdx, sIdx) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor.splice(sIdx, 1);
    setField("shiftPattern", list);
  };

  // Calculate total booking price
  const calculateTotalPrice = useCallback(() => {
    if (!priceData || !sectorsRes?.data) return 0;
    return calculateTotalPriceFromPatterns({
      shiftPattern: formik.values.shiftPattern,
      sectors: sectorsRes.data,
      isUrgent: formik.values.isUrgent,
      uplifts: priceData,
      generateDatesArray,
    });
  }, [
    formik.values.shiftPattern,
    formik.values.isUrgent,
    priceData,
    sectorsRes,
  ]);

  // Recalculate pricing whenever relevant values change
  const totalPrice = useMemo(
    () => calculateTotalPrice(),
    [calculateTotalPrice]
  );

  const pricingBreakdown = useMemo(() => {
    if (!priceData || !sectorsRes?.data) {
      return {
        workerBaseAmount: 0,
        weekendUpliftAmount: 0,
        nightUpliftAmount: 0,
        urgentUpliftAmount: 0,
        bankHolidayUpliftAmount: 0,
        upliftAmount: 0,
        holidayPayAmount: 0,
        niAmount: 0,
        pensionAmount: 0,
        levyAmount: 0,
        marginAmount: 0,
        total: 0,
      };
    }
    return calculatePricingBreakdownFromPatterns({
      shiftPattern: formik.values.shiftPattern,
      sectors: sectorsRes.data,
      isUrgent: formik.values.isUrgent,
      uplifts: priceData,
      generateDatesArray,
    });
  }, [
    formik.values.shiftPattern,
    formik.values.isUrgent,
    priceData,
    sectorsRes,
  ]);

  // Calculate total workers and discount
  // Discount calculation commented out — keep zero-discount pricing for UI/API
  // const pricingDetails = useMemo(
  //   () =>
  //     calculateDiscountPricingDetails({
  //       shiftPattern: formik.values.shiftPattern,
  //       totalPrice,
  //       discounts: discountsRes?.data || [],
  //       generateDatesArray,
  //     }),
  //   [formik.values.shiftPattern, totalPrice, discountsRes?.data]
  // );
  const pricingDetails = useMemo(() => {
    const totalWorkers = (formik.values.shiftPattern || []).reduce(
      (sum, p) => sum + (Number(p.quantity) || 0),
      0
    );
    const totalWorkerShifts = (formik.values.shiftPattern || []).reduce(
      (sum, p) => {
        const dates = generateDatesArray(
          p.startDate,
          p.endDate,
          p.excludeWeekend,
          p.excludeSpecificDates
        );
        return sum + dates.length * (Number(p.quantity) || 0);
      },
      0
    );
    return {
      originalPrice: totalPrice,
      discountAmount: 0,
      discountPercentage: 0,
      discountType: "percentage",
      finalPrice: totalPrice,
      totalWorkers,
      totalWorkerShifts,
    };
  }, [formik.values.shiftPattern, totalPrice]);

  // Step banners - REMOVED
  // const ruleText = formik.values.isUrgent
  //   ? "Urgent: earliest start is today if at least 6 hours from now; otherwise tomorrow."
  //   : "Not urgent: earliest start is the day after tomorrow (≈48 hours from now).";

  /* ======================
     Render
  ====================== */
  return (
    <div className="page-content">
      <Container fluid>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          {/* ========== STEP 1: Custom Card Design ========== */}
          {step === 1 && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">
                  Requesting staff made simple.
                </h2>
                <p className="text-muted mb-0">
                  Use this form to book JobArk workers for your upcoming shifts.
                  Specify roles, worker count, shift times, and any special
                  requirements. Our system will match you with vetted workers
                  and provide an instant cost estimate.
                </p>
              </div>

              {/* Purchase Order Section */}
              <div className="section-gray">
                <h5 className="section-header">
                  Does this booking require a Purchase Order?
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <Input
                          type="radio"
                          name="poRequired"
                          id="po-yes"
                          checked={formik.values.hasPORequired === true}
                          onChange={() => setField("hasPORequired", true)}
                          className="form-check-input"
                        />
                        <Label for="po-yes" className="form-check-label">
                          Yes
                        </Label>
                      </div>
                      <div className="form-check">
                        <Input
                          type="radio"
                          name="poRequired"
                          id="po-no"
                          checked={formik.values.hasPORequired === false}
                          onChange={() => {
                            setField("hasPORequired", false);
                            // Clear PO fields when not required
                            setField("poReference", "");
                            setField("poDocument", null);
                          }}
                          className="form-check-input"
                        />
                        <Label for="po-no" className="form-check-label">
                          No
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>

                {formik.values.hasPORequired && (
                  <Row className="g-3 mt-3">
                    <Col md={6}>
                      <Label>Purchase Order Reference</Label>
                      <Input
                        name="poReference"
                        value={formik.values.poReference}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.poReference &&
                          !!formik.errors.poReference
                        }
                        placeholder="PO-154151681"
                      />
                      <FormFeedback>{formik.errors.poReference}</FormFeedback>
                    </Col>
                    <Col md={6}>
                      <Label>Official PO document</Label>
                      <Input
                        type="file"
                        onChange={(e) =>
                          setField(
                            "poDocument",
                            e.currentTarget.files?.[0] || null
                          )
                        }
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.poDocument &&
                          !!formik.errors.poDocument
                        }
                      />
                      {formik.touched.poDocument &&
                        formik.errors.poDocument && (
                          <div className="invalid-feedback d-block">
                            {formik.errors.poDocument}
                          </div>
                        )}
                    </Col>
                  </Row>
                )}
              </div>

              {/* Set Location Section */}
              <div className="section-white">
                <h5 className="section-header">Set Location</h5>
                <Row className="g-3">
                  <Col md={6}>
                    <Label>Site Name</Label>
                    <Input
                      name="location.siteName"
                      value={formik.values.location.siteName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.location?.siteName &&
                        !!formik.errors.location?.siteName
                      }
                      placeholder="John Doe"
                    />
                    <FormFeedback>
                      {formik.errors.location?.siteName}
                    </FormFeedback>
                  </Col>

                  <Col md={6}>
                    <Label>Search Address</Label>
                    {isLoaded ? (
                      <>
                        <Autocomplete
                          onLoad={onLoadAutocomplete}
                          onPlaceChanged={onPlaceChanged}
                          options={{
                            fields: ["formatted_address", "geometry", "name"],
                          }}
                        >
                          <Input
                            placeholder="Type and pick an address"
                            value={formik.values.location.searchAddress}
                            onChange={(e) => {
                              const v = e.target.value;
                              const patch = { searchAddress: v };
                              if (
                                !v.trim() ||
                                v.trim() !==
                                  formik.values.location.fullAddress?.trim()
                              ) {
                                patch.fullAddress = "";
                              }
                              void patchLocation(patch, { validate: false });
                              if (formik.errors.location) {
                                setFormikLocationErrors(formik, null);
                              }
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                "location.searchAddress",
                                true,
                                false
                              );
                              formik.setFieldTouched(
                                "location.fullAddress",
                                true,
                                false
                              );
                              const loc = formik.values.location;
                              if (!loc.fullAddress?.trim()) {
                                void validateFormikLocation(
                                  formik,
                                  locationSchema,
                                  loc
                                );
                              }
                            }}
                            name="location.searchAddress"
                            invalid={showLocationAddressError}
                          />
                        </Autocomplete>
                        {showLocationAddressError && (
                          <div className="invalid-feedback d-block">
                            {locationAddressError}
                          </div>
                        )}
                      </>
                    ) : (
                      <Input disabled placeholder="Loading map..." />
                    )}
                  </Col>

                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                      <Label className="mb-0">Full Address</Label>
                      <Button
                        color="secondary"
                        type="button"
                        size="sm"
                        onClick={useCurrentLocation}
                      >
                        Use Current Location
                      </Button>
                    </div>
                    <div
                      className={`form-control bg-light ${
                        showLocationAddressError ? "is-invalid" : ""
                      }`}
                      aria-readonly="true"
                    >
                      {formik.values.location.fullAddress?.trim() ? (
                        formik.values.location.fullAddress
                      ) : (
                        <span className="text-muted">
                          Populated when you select an address, use current
                          location, or move the map marker
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={12}>
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={center}
                        zoom={12}
                        options={{
                          streetViewControl: false,
                          mapTypeControl: false,
                        }}
                        onClick={(e) => {
                          if (!e.latLng) return;
                          const lat = e.latLng.lat();
                          const lng = e.latLng.lng();
                          setField("location.latitude", lat);
                          setField("location.longitude", lng);
                          reverseGeocodeToAddress(lat, lng);
                        }}
                      >
                        <Marker
                          position={center}
                          draggable
                          onDragEnd={onMarkerDragEnd}
                          title="Drag to precise location"
                        />
                      </GoogleMap>
                    ) : (
                      <div className="border rounded p-4 text-muted">
                        Loading map…
                      </div>
                    )}
                  </Col>

                  {/* Latitude / Longitude hidden from UI — still stored in form state for map & payload
                  <Col md={4}>
                    <Label>Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      name="location.latitude"
                      value={formik.values.location.latitude}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.location?.latitude &&
                        !!formik.errors.location?.latitude
                      }
                      placeholder="51.5199835418162"
                    />
                    <FormFeedback>
                      {formik.errors.location?.latitude}
                    </FormFeedback>
                  </Col>

                  <Col md={4}>
                    <Label>Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      name="location.longitude"
                      value={formik.values.location.longitude}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.location?.longitude &&
                        !!formik.errors.location?.longitude
                      }
                      placeholder="-0.03160791153367537"
                    />
                    <FormFeedback>
                      {formik.errors.location?.longitude}
                    </FormFeedback>
                  </Col>

                  <Col md={4} className="d-flex align-items-end">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={useCurrentLocation}
                      className="w-100"
                    >
                      Use Current Location
                    </Button>
                  </Col>
                  */}

                  {/* Timezone UI removed — UK only (Europe/London hardcoded)
                  <Col md={12}>
                    <Label>Timezone</Label>
                    <Select
                      options={[
                        {
                          value: "Europe/London",
                          label: "Europe/London (GMT)",
                        },
                        {
                          value: "Europe/Dublin",
                          label: "Europe/Dublin (GMT)",
                        },
                        { value: "Europe/Paris", label: "Europe/Paris (CET)" },
                        {
                          value: "Europe/Berlin",
                          label: "Europe/Berlin (CET)",
                        },
                        {
                          value: "America/New_York",
                          label: "America/New_York (EST)",
                        },
                        {
                          value: "America/Los_Angeles",
                          label: "America/Los_Angeles (PST)",
                        },
                        { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
                        { value: "Asia/Karachi", label: "Asia/Karachi (PKT)" },
                      ]}
                      value={{
                        value: formik.values.timezone,
                        label: formik.values.timezone,
                      }}
                      onChange={(opt) =>
                        setField("timezone", opt?.value || "Europe/London")
                      }
                      onBlur={() => formik.setFieldTouched("timezone", true)}
                      placeholder="Select timezone"
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 99999,
                          backgroundColor: "#fff",
                        }),
                      }}
                    />
                    {formik.touched.timezone && formik.errors.timezone && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.timezone}
                      </div>
                    )}
                  </Col>
                  */}
                </Row>
              </div>

              {/* Notes Section */}
              <div className="section-white">
                <h5 className="section-header">Notes</h5>
                <Row className="g-3">
                  <Col md={12}>
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      name="notes"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.notes && !!formik.errors.notes}
                      placeholder="Please add any relevant notes for this booking."
                    />
                    <FormFeedback>{formik.errors.notes}</FormFeedback>
                  </Col>
                </Row>
              </div>

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <Button
                  type="button"
                  color="secondary"
                  className="btn-next-full"
                  onClick={nextStep}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 2: Custom Card Design ========== */}
          {step === 2 && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">
                  Configure Your Shifts
                </h2>
                <p className="text-muted mb-0">
                  Set up your shift patterns, worker requirements, and
                  scheduling details.{" "}
                  {formik.values.isUrgent
                    ? "Urgent bookings allow one shift pattern only."
                    : "You can add up to 3 different shift patterns for this booking."}
                </p>
              </div>

              {/* Booking Type Section */}
              <div className="section-gray">
                <h5 className="section-header">Booking Type</h5>
                <p className="text-muted mb-3">
                  Select &quot;Urgent&quot; if the shift is scheduled to start
                  within the next 24 hours.
                </p>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="urgent"
                    name="isUrgent"
                    checked={formik.values.isUrgent}
                    onChange={(e) => {
                      formik.handleChange(e);

                      if (e.target.checked) {
                        let copy = [...formik.values.shiftPattern];
                        if (copy.length > 1) {
                          copy = [copy[0]];
                          toastr.info(
                            "Urgent bookings allow one shift pattern. Extra patterns were removed."
                          );
                        }
                        copy.forEach((pattern, idx) => {
                          if (
                            pattern.startTime &&
                            pattern.endTime &&
                            pattern.startDate
                          ) {
                            const startTime = pattern.startTime;
                            const endTime = pattern.endTime;

                            // If end time is earlier than start time, it's an overnight shift
                            if (endTime < startTime) {
                              // For urgent bookings, end date should be next day for overnight shifts
                              const startDate = dayjs(pattern.startDate);
                              const nextDay = startDate
                                .add(1, "day")
                                .format("YYYY-MM-DD");
                              copy[idx].endDate = nextDay;
                            } else {
                              // Same day shift, end date should match start date
                              copy[idx].endDate = pattern.startDate;
                            }

                            // Update dates array
                            copy[idx].dates = updateDatesArray(idx, copy[idx]);
                          }
                        });
                        setField("shiftPattern", copy);
                      }

                      // Trigger validation for all shift patterns when urgent status changes
                      setTimeout(() => {
                        formik.values.shiftPattern.forEach((pattern, idx) => {
                          formik.validateField(`shiftPattern.${idx}.startDate`);
                          formik.validateField(`shiftPattern.${idx}.startTime`);
                          formik.validateField(`shiftPattern.${idx}.endDate`);
                        });
                      }, 100);
                    }}
                    className="form-check-input"
                  />
                  <Label for="urgent" className="form-check-label">
                    Urgent
                  </Label>
                </div>
              </div>

              {/* Shift Patterns */}
              {(formik.values.shiftPattern || []).map((p, idx) => {
                const pTouched = formik.touched.shiftPattern?.[idx] || {};
                const pErrors = formik.errors.shiftPattern?.[idx] || {};

                const roleValue =
                  roleOptions.find((o) => o.value === p.roleSlug) || null;

                return (
                  <Card
                    className="mb-3 section-dark-gray"
                    key={`pattern-${idx}`}
                  >
                    <CardHeader className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>Shift Pattern {idx + 1}</strong>
                        {!formik.values.isUrgent &&
                          formik.values.shiftPattern.length > 1 && (
                            <Badge color="light" className="ms-2">
                              {
                                generateDatesArray(
                                  p.startDate,
                                  p.endDate,
                                  p.excludeWeekend,
                                  p.excludeSpecificDates
                                ).length
                              }{" "}
                              date
                              {generateDatesArray(
                                p.startDate,
                                p.endDate,
                                p.excludeWeekend,
                                p.excludeSpecificDates
                              ).length !== 1
                                ? "s"
                                : ""}
                            </Badge>
                          )}
                      </div>
                      <div className="d-flex gap-2">
                        {!formik.values.isUrgent &&
                          formik.values.shiftPattern.length > 1 && (
                            <Button
                              size="sm"
                              color="outline-danger"
                              onClick={() => removePattern(idx)}
                              className="btn-cross"
                            >
                              ×
                            </Button>
                          )}
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row className="g-3">
                        <Col md={6}>
                          <Label>Sector</Label>
                          <Select
                            isDisabled={sectorsLoading}
                            options={sectorOptions}
                            value={
                              sectorOptions.find(
                                (opt) => opt.value === p.sectorSlug
                              ) || null
                            }
                            onChange={(opt) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].sectorSlug = opt?.value || "";
                              copy[idx].roleSlug = ""; // Reset role when sector changes
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.sectorSlug`,
                                true
                              );
                            }}
                            placeholder={
                              sectorsLoading ? "Loading…" : "Select sector"
                            }
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                                backgroundColor: "#fff",
                              }),
                            }}
                          />
                          {pTouched?.sectorSlug && pErrors?.sectorSlug && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.sectorSlug}
                            </div>
                          )}
                        </Col>

                        <Col md={6}>
                          <Label>Role</Label>
                          <Select
                            isDisabled={sectorsLoading || !p.sectorSlug}
                            options={roleOptions.filter(
                              (opt) => opt.sectorSlug === p.sectorSlug
                            )}
                            value={roleValue}
                            onChange={(opt) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].roleSlug = opt?.value || "";
                              copy[idx].sectorSlug = opt?.sectorSlug || "";
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.roleSlug`,
                                true
                              );
                            }}
                            placeholder={
                              !p.sectorSlug
                                ? "Select sector first"
                                : sectorsLoading
                                ? "Loading…"
                                : "Select role"
                            }
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                                backgroundColor: "#fff",
                              }),
                            }}
                          />
                          {pTouched?.roleSlug && pErrors?.roleSlug && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.roleSlug}
                            </div>
                          )}
                        </Col>

                        <Col md={12}>
                          <Label>PPE Provided?</Label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <Input
                                type="radio"
                                id={`ppe-yes-${idx}`}
                                name={`ppe-provided-${idx}`}
                                value="yes"
                                checked={p.hasPPEProvided === "yes"}
                                onClick={() => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].hasPPEProvided = "yes";
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`ppe-yes-${idx}`}
                              >
                                Yes
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id={`ppe-not-required-${idx}`}
                                name={`ppe-provided-${idx}`}
                                value="not_required"
                                checked={p.hasPPEProvided === "not_required"}
                                onClick={() => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].hasPPEProvided = "not_required";
                                  copy[idx].ppeInstruction = "";
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`ppe-not-required-${idx}`}
                              >
                                Not Required
                              </Label>
                            </div>
                          </div>
                          {pTouched?.hasPPEProvided &&
                            pErrors?.hasPPEProvided && (
                              <div className="invalid-feedback d-block">
                                {pErrors?.hasPPEProvided}
                              </div>
                            )}
                        </Col>

                        {p.hasPPEProvided === "yes" && (
                          <Col md={12}>
                            <Label>PPE Details</Label>
                            <Input
                              name={`shiftPattern.${idx}.ppeInstruction`}
                              value={
                                formik.values.shiftPattern[idx]
                                  ?.ppeInstruction ?? ""
                              }
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `shiftPattern.${idx}.ppeInstruction`,
                                  e.target.value
                                )
                              }
                              onBlur={formik.handleBlur}
                              invalid={Boolean(
                                formik.touched.shiftPattern?.[idx]
                                  ?.ppeInstruction &&
                                  formik.errors.shiftPattern?.[idx]
                                    ?.ppeInstruction
                              )}
                              placeholder="Helmet, gloves"
                            />
                            {formik.touched.shiftPattern?.[idx]
                              ?.ppeInstruction &&
                              formik.errors.shiftPattern?.[idx]
                                ?.ppeInstruction && (
                                <div className="invalid-feedback d-block">
                                  {
                                    formik.errors.shiftPattern[idx]
                                      .ppeInstruction
                                  }
                                </div>
                              )}
                          </Col>
                        )}

                        <Col md={4}>
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={p.startDate}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].startDate = e.target.value;

                              // For urgent bookings, check if end date needs adjustment based on time fields
                              if (
                                formik.values.isUrgent &&
                                copy[idx].startTime &&
                                copy[idx].endTime
                              ) {
                                const startTime = copy[idx].startTime;
                                const endTime = copy[idx].endTime;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }
                              }

                              copy[idx].dates = updateDatesArray(
                                idx,
                                copy[idx]
                              );
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.endDate`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.startDate`,
                                true
                              );
                            }}
                            className={
                              pTouched?.startDate && pErrors?.startDate
                                ? "is-invalid"
                                : ""
                            }
                            min={toISODate(dayjs())}
                          />
                          {pTouched?.startDate && pErrors?.startDate && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.startDate}
                            </div>
                          )}
                        </Col>

                        <Col md={4}>
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={p.endDate}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].endDate = e.target.value;
                              copy[idx].dates = updateDatesArray(
                                idx,
                                copy[idx]
                              );
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.endDate`,
                                true
                              );
                            }}
                            className={
                              pTouched?.endDate && pErrors?.endDate
                                ? "is-invalid"
                                : ""
                            }
                            min={p.startDate || toISODate(dayjs())}
                            disabled={formik.values.isUrgent}
                          />
                          {pTouched?.endDate && pErrors?.endDate && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.endDate}
                            </div>
                          )}
                        </Col>

                        <Col md={4} className="d-flex align-items-end">
                          {!formik.values.isUrgent && (
                            <div className="form-check">
                              <Input
                                type="checkbox"
                                id={`exclude-weekend-${idx}`}
                                checked={p.excludeWeekend}
                                onChange={(e) => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].excludeWeekend = e.target.checked;
                                  copy[idx].dates = updateDatesArray(
                                    idx,
                                    copy[idx]
                                  );
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`exclude-weekend-${idx}`}
                              >
                                Exclude Weekend
                              </Label>
                            </div>
                          )}
                        </Col>

                        {!formik.values.isUrgent && (
                          <Col md={12}>
                            <Label>Exclude specific dates (optional)</Label>
                            <ExcludeDatesPicker
                              startDate={p.startDate}
                              endDate={p.endDate}
                              excludeWeekend={p.excludeWeekend}
                              selectedDates={p.excludeSpecificDates}
                              onDatesChange={(dates) => {
                                const copy = [...formik.values.shiftPattern];
                                copy[idx].excludeSpecificDates = dates;
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                                setField("shiftPattern", copy);
                              }}
                            />
                          </Col>
                        )}

                        <Col md={3}>
                          <Label>Start Time</Label>
                          <Input
                            type="time"
                            value={p.startTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].startTime = e.target.value;

                              // Check for overnight shift and adjust end date if needed
                              if (
                                formik.values.isUrgent &&
                                copy[idx].endTime &&
                                e.target.value
                              ) {
                                const startTime = e.target.value;
                                const endTime = copy[idx].endTime;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }

                                // Update dates array
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                              }

                              // auto-adjust default break to rule if needed
                              const hours = diffHours(
                                copy[idx].startTime,
                                copy[idx].endTime
                              );
                              const minBreak = minBreakMinutesFor(hours);
                              if (
                                hhmmToMinutes(copy[idx].breakTime) < minBreak
                              ) {
                                copy[idx].breakTime = minutesToHHMM(minBreak);
                              }
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.startTime`,
                                true
                              );
                            }}
                            className={
                              pTouched?.startTime && pErrors?.startTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.startTime && pErrors?.startTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.startTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>End Time</Label>
                          <Input
                            type="time"
                            value={p.endTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].endTime = e.target.value;

                              // Check for overnight shift and adjust end date if needed
                              if (
                                formik.values.isUrgent &&
                                copy[idx].startTime &&
                                e.target.value
                              ) {
                                const startTime = copy[idx].startTime;
                                const endTime = e.target.value;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }

                                // Update dates array
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                              }

                              const hours = diffHours(
                                copy[idx].startTime,
                                copy[idx].endTime
                              );
                              const minBreak = minBreakMinutesFor(hours);
                              if (
                                hhmmToMinutes(copy[idx].breakTime) < minBreak
                              ) {
                                copy[idx].breakTime = minutesToHHMM(minBreak);
                              }
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.endTime`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.endTime`,
                                true
                              );
                            }}
                            className={
                              pTouched?.endTime && pErrors?.endTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.endTime && pErrors?.endTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.endTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>Break Time (HH:MM)</Label>
                          <Input
                            type="text"
                            value={p.breakTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].breakTime = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.breakTime`,
                                true
                              );
                            }}
                            placeholder="00:30"
                            className={
                              pTouched?.breakTime && pErrors?.breakTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          <small className="text-muted d-block">
                            Rule: &lt;6h → 0m; &gt;6h → ≥20m; &gt;9h → ≥30m
                          </small>
                          {pTouched?.breakTime && pErrors?.breakTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.breakTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>Number of workers</Label>
                          <Input
                            type="number"
                            min={1}
                            value={p.quantity}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].quantity = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.quantity`,
                                true
                              );
                            }}
                            className={
                              pTouched?.quantity && pErrors?.quantity
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.quantity && pErrors?.quantity && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.quantity}
                            </div>
                          )}
                        </Col>

                        <Col md={12}>
                          <Label>Experience level *</Label>
                          <Select
                            isMulti
                            options={experienceLevelOptions}
                            value={experienceLevelOptions.filter((opt) =>
                              (p.experienceLevel || []).includes(opt.value)
                            )}
                            onChange={(opts) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].experienceLevel = (opts || []).map(
                                (o) => o.value
                              );
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.experienceLevel`,
                                true
                              );
                            }}
                            placeholder="Select experience level"
                            menuPortalTarget={document.body}
                            classNamePrefix="rs"
                            styles={reactSelectMenuPortalStyles}
                          />
                          {pTouched?.experienceLevel &&
                            pErrors?.experienceLevel && (
                              <div className="invalid-feedback d-block">
                                {pErrors.experienceLevel}
                              </div>
                            )}
                        </Col>

                        <Col md={12}>
                          <Label>Mandatory Clearances Required *</Label>
                          <Select
                            isMulti
                            options={complianceCertOptions}
                            value={complianceCertOptions.filter((opt) =>
                              (p.certSlugs || []).includes(opt.value)
                            )}
                            onChange={(opts) => {
                              const copy = [...formik.values.shiftPattern];
                              const nextSlugs = (opts || []).map(
                                (o) => o.value
                              );
                              copy[idx].certSlugs = nextSlugs;
                              if (
                                !patternHasDbsClearance(
                                  nextSlugs,
                                  complianceCertsList
                                )
                              ) {
                                copy[idx].dbsType = "";
                                copy[idx].dbsWorkForce = "";
                              }
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.certSlugs`,
                                true
                              );
                            }}
                            placeholder="Select mandatory clearances"
                            isDisabled={complianceCertOptions.length === 0}
                            menuPortalTarget={document.body}
                            classNamePrefix="rs"
                            styles={reactSelectMenuPortalStyles}
                          />
                          {pTouched?.certSlugs && pErrors?.certSlugs && (
                            <div className="invalid-feedback d-block">
                              {pErrors.certSlugs}
                            </div>
                          )}
                        </Col>

                        {patternHasDbsClearance(
                          p.certSlugs,
                          complianceCertsList
                        ) && (
                          <>
                            <Col md={6}>
                              <Label>DBS type *</Label>
                              <Input
                                type="select"
                                value={p.dbsType || ""}
                                onChange={(e) => {
                                  const dbsType = e.target.value;
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].dbsType = dbsType;
                                  if (!dbsTypeRequiresWorkforce(dbsType)) {
                                    copy[idx].dbsWorkForce = "";
                                  }
                                  setField("shiftPattern", copy);
                                }}
                                onBlur={() => {
                                  formik.setFieldTouched(
                                    `shiftPattern.${idx}.dbsType`,
                                    true
                                  );
                                }}
                                invalid={
                                  !!(pTouched?.dbsType && pErrors?.dbsType)
                                }
                              >
                                <option value="">Select DBS type</option>
                                {DBS_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Input>
                              {pTouched?.dbsType && pErrors?.dbsType && (
                                <FormFeedback className="d-block">
                                  {pErrors.dbsType}
                                </FormFeedback>
                              )}
                            </Col>
                            {dbsTypeRequiresWorkforce(p.dbsType) && (
                              <Col md={6}>
                                <Label>Workforce *</Label>
                                <Input
                                  type="select"
                                  value={p.dbsWorkForce || ""}
                                  onChange={(e) => {
                                    const copy = [
                                      ...formik.values.shiftPattern,
                                    ];
                                    copy[idx].dbsWorkForce = e.target.value;
                                    setField("shiftPattern", copy);
                                  }}
                                  onBlur={() => {
                                    formik.setFieldTouched(
                                      `shiftPattern.${idx}.dbsWorkForce`,
                                      true
                                    );
                                  }}
                                  invalid={
                                    !!(
                                      pTouched?.dbsWorkForce &&
                                      pErrors?.dbsWorkForce
                                    )
                                  }
                                >
                                  <option value="">Select workforce</option>
                                  {WORKFORCE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Input>
                                {pTouched?.dbsWorkForce &&
                                  pErrors?.dbsWorkForce && (
                                    <FormFeedback className="d-block">
                                      {pErrors.dbsWorkForce}
                                    </FormFeedback>
                                  )}
                              </Col>
                            )}
                          </>
                        )}

                        {/* Shift Supervisor Details */}
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <Label className="mb-0">
                              <strong>Shift Supervisor Details</strong>
                            </Label>
                            <Button
                              size="sm"
                              color="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addSupervisor(idx);
                              }}
                              type="button"
                            >
                              Add Supervisor
                            </Button>
                          </div>
                          {(p.supervisor || []).map((s, sIdx) => {
                            const sTouched = pTouched?.supervisor?.[sIdx] || {};
                            const sErrors = pErrors?.supervisor?.[sIdx] || {};
                            const isExisting = s.source === "existing";
                            const rowSupervisorOptions =
                              supervisorOptionsForShift(idx, sIdx);
                            const selectedSupervisor =
                              rowSupervisorOptions.find(
                                (o) => o.value === s.supervisorSlug
                              ) ||
                              supervisorOptions.find(
                                (o) => o.value === s.supervisorSlug
                              ) ||
                              null;
                            const duplicateInShift =
                              isDuplicateSupervisorInShift(
                                p.supervisor || [],
                                sIdx
                              );
                            return (
                              <div
                                key={sIdx}
                                className="mb-3 p-3 border rounded bg-white"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted fw-semibold">
                                    Supervisor {sIdx + 1}
                                  </small>
                                  {p.supervisor.length > 1 && (
                                    <Button
                                      size="sm"
                                      color="outline-danger"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeSupervisor(idx, sIdx);
                                      }}
                                      type="button"
                                      className="btn-cross"
                                    >
                                      ×
                                    </Button>
                                  )}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                  <Button
                                    type="button"
                                    size="sm"
                                    color={
                                      isExisting
                                        ? "secondary"
                                        : "outline-secondary"
                                    }
                                    onClick={() =>
                                      setSupervisorSource(idx, sIdx, "existing")
                                    }
                                  >
                                    Select existing
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    color={
                                      !isExisting
                                        ? "secondary"
                                        : "outline-secondary"
                                    }
                                    onClick={() =>
                                      setSupervisorSource(idx, sIdx, "new")
                                    }
                                  >
                                    Add new
                                  </Button>
                                </div>
                                {isExisting ? (
                                  <Row className="g-2">
                                    <Col md={12}>
                                      <Label className="small mb-1">
                                        Supervisor
                                      </Label>
                                      <Select
                                        options={rowSupervisorOptions}
                                        value={selectedSupervisor}
                                        onChange={(opt) =>
                                          selectExistingSupervisor(
                                            idx,
                                            sIdx,
                                            opt
                                          )
                                        }
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.supervisorSlug`,
                                            true
                                          );
                                        }}
                                        isClearable
                                        isLoading={supervisorsLoading}
                                        placeholder={
                                          supervisorsLoading
                                            ? "Loading supervisors…"
                                            : rowSupervisorOptions.length
                                            ? "Select a supervisor"
                                            : "No other supervisors available"
                                        }
                                        styles={reactSelectMenuPortalStyles}
                                        className={
                                          (sTouched?.supervisorSlug &&
                                            sErrors?.supervisorSlug) ||
                                          duplicateInShift
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.supervisorSlug &&
                                        sErrors?.supervisorSlug && (
                                          <div className="invalid-feedback d-block">
                                            {sErrors.supervisorSlug}
                                          </div>
                                        )}
                                      {!sErrors?.supervisorSlug &&
                                        duplicateInShift && (
                                          <div className="invalid-feedback d-block">
                                            This supervisor is already added to
                                            this shift
                                          </div>
                                        )}
                                    </Col>
                                    {selectedSupervisor && (
                                      <Col md={12}>
                                        <div className="small text-muted">
                                          {s.name} · {s.email} · +
                                          {String(s.mobile || "").replace(
                                            /^\+/,
                                            ""
                                          )}
                                        </div>
                                      </Col>
                                    )}
                                  </Row>
                                ) : (
                                  <Row className="g-2 align-items-start">
                                    <Col md={4}>
                                      <Input
                                        placeholder="Name"
                                        value={s.name || ""}
                                        onChange={(e) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].name =
                                            e.target.value;
                                          setField("shiftPattern", copy);
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.name`,
                                            true
                                          );
                                        }}
                                        className={
                                          sTouched?.name && sErrors?.name
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.name && sErrors?.name && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.name}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={4}>
                                      <PhoneInput
                                        country="gb"
                                        enableSearch
                                        containerClass="w-100"
                                        inputProps={{
                                          name: `shiftPattern[${idx}].supervisor[${sIdx}].mobile`,
                                        }}
                                        inputClass={
                                          sTouched?.mobile && sErrors?.mobile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        value={s.mobile || ""}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.mobile`,
                                            true
                                          );
                                        }}
                                        onChange={(v) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].mobile = v;
                                          setField("shiftPattern", copy);
                                        }}
                                      />
                                      {sTouched?.mobile && sErrors?.mobile && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.mobile}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={4}>
                                      <Input
                                        type="email"
                                        placeholder="Email (required)"
                                        value={s.email || ""}
                                        onChange={(e) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].email =
                                            e.target.value;
                                          setField("shiftPattern", copy);
                                          setTimeout(() => {
                                            formik.validateField(
                                              `shiftPattern.${idx}.supervisor`
                                            );
                                          }, 0);
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.email`,
                                            true
                                          );
                                        }}
                                        className={
                                          sTouched?.email && sErrors?.email
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.email && sErrors?.email && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.email}
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                )}
                              </div>
                            );
                          })}
                        </Col>

                        {/* Shift Details */}
                        <Col md={12}>
                          <Label>
                            <strong>Shift Details</strong>
                          </Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={p.shiftDetails}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].shiftDetails = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.shiftDetails`,
                                true
                              );
                            }}
                            className={
                              pTouched?.shiftDetails && pErrors?.shiftDetails
                                ? "is-invalid"
                                : ""
                            }
                            placeholder="Main entrance, security required etc"
                          />
                          {pTouched?.shiftDetails && pErrors?.shiftDetails && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.shiftDetails}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                );
              })}

              {/* Create Another Pattern Button */}
              {formik.values.shiftPattern.length <
                maxShiftPatternsForBooking(formik.values.isUrgent) && (
                <div className="mb-3">
                  <Button
                    color="warning"
                    size="lg"
                    onClick={addPattern}
                    className="w-100"
                  >
                    Create Another Pattern
                  </Button>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    type="button"
                    onClick={prevStep}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#000000",
                      fontWeight: "bold",
                      padding: "0.5rem 0",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      fontSize: "1rem",
                    }}
                  >
                    ← Back
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    onClick={nextStep}
                    style={{
                      backgroundColor: "#2ca6a4",
                      borderColor: "#2ca6a4",
                      borderRadius: "8px",
                      padding: "0.5rem 1.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ========== STEP 3: Custom Card Design ========== */}
          {step === 3 && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">Review & Confirm</h2>
                <p className="text-muted mb-0">
                  Please review your booking details and confirm your agreement
                  to our terms and conditions before submitting your request.
                </p>
              </div>

              {/* Booking Summary Card */}
              <Card className="mb-4">
                <CardHeader className="bg-primary">
                  <h6 className="mb-0 fw-semibold text-white">
                    Booking Summary
                  </h6>
                </CardHeader>
                <CardBody className="bg-light">
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-calendar me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">
                            Booking Type
                          </small>
                          <span className="fw-medium">
                            {formik.values.isUrgent ? "Urgent" : "Non Urgent"}
                          </span>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-map me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">Location</small>
                          <span className="fw-medium">
                            {formik.values.location.siteName || "—"}
                          </span>
                        </div>
                      </div>
                    </Col>
                    {/* Timezone review removed — UK only
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-time me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">Timezone</small>
                          <span className="fw-medium">
                            {formik.values.timezone}
                          </span>
                        </div>
                      </div>
                    </Col>
                    */}
                    {formik.values.hasPORequired && (
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <i className="bx bx-file me-2 text-primary"></i>
                          <div>
                            <small className="text-muted d-block">
                              PO Reference
                            </small>
                            <span className="fw-medium">
                              {formik.values.poReference || "—"}
                            </span>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                  {formik.values.location.fullAddress && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <small className="text-muted d-block">Full Address</small>
                      <span className="fw-medium">
                        {formik.values.location.fullAddress}
                      </span>
                    </div>
                  )}
                  {formik.values.notes && (
                    <div className="mt-3">
                      <small className="text-muted d-block">Notes</small>
                      <span className="fw-medium">{formik.values.notes}</span>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Shift Patterns */}
              {formik.values.shiftPattern.map((p, i) => {
                const generatedDates = generateDatesArray(
                  p.startDate,
                  p.endDate,
                  p.excludeWeekend,
                  p.excludeSpecificDates
                );

                return (
                  <Card key={i} className="mb-4">
                    <CardHeader className="bg-primary">
                      <h6 className="mb-0 fw-semibold text-white">
                        Shift Pattern {i + 1}
                      </h6>
                    </CardHeader>
                    <CardBody className="bg-light">
                      {/* Pattern Details */}
                      <div className="mb-4">
                        <div className="mb-2">
                          • <strong>Booking Location:</strong>{" "}
                          {formik.values.location.fullAddress || "—"}
                        </div>
                        {formik.values.hasPORequired && (
                          <div className="mb-2">
                            • <strong>PO Number:</strong>{" "}
                            {formik.values.poReference || "—"}
                          </div>
                        )}
                        <div className="mb-2">
                          • <strong>Sector:</strong>{" "}
                          {sectorOptions.find((o) => o.value === p.sectorSlug)
                            ?.label ||
                            p.sectorSlug ||
                            "—"}
                        </div>
                        <div className="mb-2">
                          • <strong>Role:</strong>{" "}
                          {(() => {
                            const roleLabel = roleOptions.find(
                              (o) => o.value === p.roleSlug
                            )?.label;
                            if (!roleLabel) return p.roleSlug || "—";
                            const sep = " — ";
                            const sepIdx = roleLabel.indexOf(sep);
                            return sepIdx >= 0
                              ? roleLabel.slice(0, sepIdx)
                              : roleLabel;
                          })()}
                        </div>
                        <div className="mb-2">
                          • <strong>Mandatory Clearances:</strong>{" "}
                          {(p.certSlugs || []).length
                            ? (p.certSlugs || [])
                                .map(
                                  (slug) =>
                                    complianceCertOptions.find(
                                      (o) => o.value === slug
                                    )?.label || slug
                                )
                                .join(", ")
                            : "—"}
                        </div>
                        {patternHasDbsClearance(
                          p.certSlugs,
                          complianceCertsList
                        ) &&
                          p.dbsType && (
                            <div className="mb-2">
                              • <strong>DBS type:</strong>{" "}
                              {DBS_TYPE_OPTIONS.find(
                                (o) => o.value === p.dbsType
                              )?.label || p.dbsType}
                              {dbsTypeRequiresWorkforce(p.dbsType) &&
                                p.dbsWorkForce && (
                                  <>
                                    {" "}
                                    (
                                    {WORKFORCE_OPTIONS.find(
                                      (o) => o.value === p.dbsWorkForce
                                    )?.label || p.dbsWorkForce}
                                    )
                                  </>
                                )}
                            </div>
                          )}
                        <div className="mb-2">
                          • <strong>PPE Provided:</strong>{" "}
                          {p.hasPPEProvided === "yes"
                            ? p.ppeInstruction || "Yes"
                            : "Not Required"}
                        </div>
                        {p.supervisor.map((s, sIdx) => (
                          <div key={sIdx} className="mb-2">
                            • <strong>Supervisor {sIdx + 1}:</strong>{" "}
                            {s.source === "existing" && s.supervisorSlug
                              ? `${s.name || "Existing supervisor"} (saved)`
                              : `${s.name} | Phone: ${s.mobile}${
                                  s.email ? ` | Email: ${s.email}` : ""
                                }`}
                          </div>
                        ))}
                        <div className="mb-2">
                          • <strong>Shift Details:</strong>{" "}
                          {p.shiftDetails || "—"}
                        </div>
                        <div className="mb-2">
                          • <strong>Booking type:</strong>{" "}
                          {formik.values.isUrgent ? "Urgent" : "Non Urgent"}
                        </div>
                      </div>

                      {/* Dates Table */}
                      {generatedDates.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="custom-secondary-header">
                              <tr>
                                <th className="text-white fw-bold">Date</th>
                                <th className="text-white fw-bold">Time</th>
                                <th className="text-white fw-bold">Break</th>
                                <th className="text-white fw-bold">
                                  # of workers
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {generatedDates.map((date, dateIdx) => (
                                <tr key={dateIdx}>
                                  <td>{dayjs(date).format("DD-M-YYYY")}</td>
                                  <td>
                                    {dayjs(`2000-01-01 ${p.startTime}`).format(
                                      "hA"
                                    )}{" "}
                                    -{" "}
                                    {dayjs(`2000-01-01 ${p.endTime}`).format(
                                      "hA"
                                    )}
                                  </td>
                                  <td>
                                    {(() => {
                                      const breakMinutes = hhmmToMinutes(
                                        p.breakTime
                                      );
                                      return breakMinutes > 0
                                        ? `${breakMinutes} MIN`
                                        : "0 MIN";
                                    })()}
                                  </td>
                                  <td>{p.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                );
              })}

              {/* Pricing Section */}
              <Card className="mb-4">
                <CardHeader className="bg-primary">
                  <h6 className="mb-0 fw-semibold text-white">
                    Pricing Summary
                  </h6>
                </CardHeader>
                <CardBody className="bg-light">
                  {upliftsQuery.isPending ? (
                    <div className="text-center p-3">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading pricing...
                        </span>
                      </div>
                      <p className="mt-2 text-muted">Calculating prices...</p>
                    </div>
                  ) : (
                    <>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="text-center p-3 border rounded">
                            <small className="text-muted d-block mb-2">
                              Estimated Cost
                            </small>
                            <h5 className="mb-0 text-dark">
                              £{pricingDetails.originalPrice.toFixed(2)}
                            </h5>
                            <small className="text-muted d-block mt-1">
                              {pricingDetails.totalWorkerShifts} worker shift
                              {pricingDetails.totalWorkerShifts !== 1
                                ? "s"
                                : ""}
                            </small>
                          </div>
                        </Col>
                        {/* Discount Applied column commented out
                        <Col md={4}>
                          <div className="text-center p-3 border rounded">
                            <small className="text-muted d-block mb-2">
                              Discount Applied
                            </small>
                            <h5
                              className={`mb-0 ${
                                pricingDetails.discountPercentage > 0
                                  ? "text-success"
                                  : "text-muted"
                              }`}
                            >
                              {pricingDetails.discountPercentage}%
                            </h5>
                            {pricingDetails.discountAmount > 0 ? (
                              <small className="text-success d-block mt-1">
                                -£{pricingDetails.discountAmount.toFixed(2)}
                              </small>
                            ) : (
                              <small className="text-muted d-block mt-1">
                                No discount
                              </small>
                            )}
                          </div>
                        </Col>
                        */}
                        <Col md={6}>
                          <div className="text-center p-3 border rounded bg-primary text-white">
                            <small className="d-block opacity-75 mb-2">
                              Total Estimated Booking Value
                            </small>
                            <h5 className="mb-0 fw-bold">
                              £{pricingDetails.finalPrice.toFixed(2)}
                            </h5>
                            <small className="d-block opacity-75 mt-1">
                              {formik.values.isUrgent ? "Urgent" : "Standard"}{" "}
                              rates
                            </small>
                          </div>
                        </Col>
                      </Row>
                      {/* Discount banners commented out
                      {pricingDetails.totalWorkers >= 10 && (
                        <div className="alert alert-success mt-3 mb-0">
                          <i className="bx bx-check-circle me-2"></i>
                          <strong>Discount Applied!</strong> You&apos;re getting{" "}
                          {pricingDetails.discountPercentage}% off for booking{" "}
                          {Number(pricingDetails.totalWorkers)} worker
                          {Number(pricingDetails.totalWorkers) !== 1 ? "s" : ""}
                          .
                          {Number(pricingDetails.totalWorkers) >= 10 &&
                            Number(pricingDetails.totalWorkers) < 14 && (
                              <span>
                                {" "}
                                Book {14 -
                                  Number(pricingDetails.totalWorkers)}{" "}
                                more worker
                                {Number(pricingDetails.totalWorkers) === 13
                                  ? ""
                                  : "s"}{" "}
                                to get a higher discount!
                              </span>
                            )}
                        </div>
                      )}
                      {pricingDetails.totalWorkers < 10 && (
                        <div className="alert alert-info mt-3 mb-0">
                          <i className="bx bx-info-circle me-2"></i>
                          Book {10 - Number(pricingDetails.totalWorkers)} more
                          worker
                          {Number(pricingDetails.totalWorkers) === 9
                            ? ""
                            : "s"}{" "}
                          to get a discount!
                        </div>
                      )}
                      */}
                    </>
                  )}
                  {!upliftsQuery.isPending && priceData && (
                    <div className="mt-3 p-3 bg-white rounded border">
                      <small className="text-muted d-block mb-2">
                        <strong>Hourly Rate Information:</strong>
                      </small>
                      <Row className="g-2">
                        <Col md={6}>
                          <small className="text-muted">
                            • Weekend uplift: £
                            {pricingBreakdown.weekendUpliftAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Night uplift: £
                            {pricingBreakdown.nightUpliftAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Urgent uplift: £
                            {pricingBreakdown.urgentUpliftAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Bank holiday uplift: £
                            {pricingBreakdown.bankHolidayUpliftAmount.toFixed(
                              2
                            )}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Holiday pay: £
                            {pricingBreakdown.holidayPayAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • NI: £{pricingBreakdown.niAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Pension: £
                            {pricingBreakdown.pensionAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Levy: £{pricingBreakdown.levyAmount.toFixed(2)}
                          </small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">
                            • Platform margin: £
                            {pricingBreakdown.marginAmount.toFixed(2)}
                          </small>
                        </Col>
                      </Row>
                      <small className="text-muted d-block mt-2">
                        <em>Note: Weekday rates vary by role</em>
                      </small>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Terms and Declarations */}
              <Card className="mb-4">
                <CardHeader className="bg-primary">
                  <h6 className="mb-0 fw-semibold text-white">
                    Terms and Declarations
                  </h6>
                </CardHeader>
                <CardBody className="bg-light">
                  <p className="mb-3">
                    By submitting this staffing request, you confirm that the
                    information provided is accurate, you have authority to
                    place this booking on behalf of your organisation, and you
                    agree to JobArk&apos;s Client Supply Agreement,{" "}
                    <a
                      href={JOBARK_PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href={JOBARK_TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </a>
                    . You understand that submitting a request does not
                    guarantee worker availability and that the booking will only
                    become confirmed once accepted by JobArk and confirmed in
                    writing.
                  </p>

                  <div className="form-check mb-3">
                    <Input
                      id="agree-authorised"
                      className="form-check-input"
                      type="checkbox"
                      name="agreements.authorised"
                      checked={formik.values.agreements.authorised}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="agree-authorised"
                    >
                      I confirm I am authorised to place bookings on behalf of
                      my organisation.
                    </Label>
                    {formik.touched.agreements?.authorised &&
                      formik.errors.agreements?.authorised && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.agreements.authorised}
                        </div>
                      )}
                  </div>

                  <div className="form-check mb-3">
                    <Input
                      id="agree-details"
                      className="form-check-input"
                      type="checkbox"
                      name="agreements.detailsAccurate"
                      checked={formik.values.agreements.detailsAccurate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <Label className="form-check-label" htmlFor="agree-details">
                      I confirm the booking details, location, role
                      requirements, working conditions and health &amp; safety
                      information provided are accurate.
                    </Label>
                    {formik.touched.agreements?.detailsAccurate &&
                      formik.errors.agreements?.detailsAccurate && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.agreements.detailsAccurate}
                        </div>
                      )}
                  </div>

                  <div className="form-check">
                    <Input
                      id="agree-legal"
                      className="form-check-input"
                      type="checkbox"
                      name="agreements.legalAgreements"
                      checked={formik.values.agreements.legalAgreements}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <Label className="form-check-label" htmlFor="agree-legal">
                      I have read and agree to the Client Supply Agreement,{" "}
                      <a
                        href={JOBARK_TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Use
                      </a>{" "}
                      and{" "}
                      <a
                        href={JOBARK_PRIVACY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </Label>
                    {formik.touched.agreements?.legalAgreements &&
                      formik.errors.agreements?.legalAgreements && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.agreements.legalAgreements}
                        </div>
                      )}
                  </div>
                </CardBody>
              </Card>

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <Row className="g-3">
                  <Col md={6}>
                    <Button
                      type="button"
                      onClick={prevStep}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#000000",
                        fontWeight: "bold",
                        padding: "0.5rem 0",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "1rem",
                      }}
                    >
                      ← Back
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      type="submit"
                      color="secondary"
                      className="btn-next-full"
                      disabled={isPending}
                    >
                      {isPending ? "Submitting..." : "Submit Staffing Request"}
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Old navigation buttons removed - now using navigation inside cards */}
        </Form>
      </Container>

      {/* Success Modal */}
      <Modal isOpen={showSuccess} toggle={handleCloseModal} centered>
        <ModalHeader toggle={handleCloseModal}>
          Staffing Request Submitted
        </ModalHeader>
        <ModalBody>
          When you clicked <strong>Submit Staffing Request</strong>, the system
          generated a unique Booking ID and sent it to the admin for review. You
          can now see it under <em>Bookings → Pending</em>. You may cancel the
          booking later, but editing won’t be possible.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCloseModal}>
            Go to Bookings
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BookingCreate;
